import React from 'react';
import { AlertTriangle, CheckCircle, Info, XCircle, Settings2 } from 'lucide-react';
import type { HealthCheckFinding, HealthCheckResult } from '@/types/healthCheck';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface HealthCheckDialogProps {
  result: HealthCheckResult;
  onClose: () => void;
  onApplyFix: (finding: HealthCheckFinding) => void;
}

export const HealthCheckDialog = ({ 
  result, 
  onClose, 
  onApplyFix 
}: HealthCheckDialogProps) => {
  const getSeverityIcon = (severity: HealthCheckFinding['severity']) => {
    switch (severity) {
      case 'error':
        return <XCircle className="h-5 w-5 text-destructive" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5" style={{ color: 'var(--warning-500)' }} />;
      case 'info':
        return <Info className="h-5 w-5 text-blue-500" />;
      case 'success':
        return <CheckCircle className="h-5 w-5" style={{ color: 'var(--success-500)' }} />;
    }
  };

  const getCategoryLabel = (category: HealthCheckFinding['category']) => {
    const labels: Record<HealthCheckFinding['category'], string> = {
      conflict: 'Configuration Conflict',
      compliance: 'Compliance Warning',
      benchmark: 'Industry Benchmark',
      usability: 'Usability Issue',
      optimization: 'Optimisation Suggestion',
    };
    return labels[category];
  };

  const getCategoryColor = (category: HealthCheckFinding['category']) => {
    const colors: Record<HealthCheckFinding['category'], string> = {
      conflict: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      compliance: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      benchmark: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      usability: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      optimization: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    };
    return colors[category];
  };

  const getScoreStyle = (score: number): React.CSSProperties => {
    if (score >= 90) return { color: 'var(--success-500)' };
    if (score >= 50) return { color: 'var(--warning-500)' };
    return {};
  };

  const getScoreLabel = (score: number) => {
    if (score >= 90) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 50) return 'Fair';
    return 'Needs Attention';
  };

  const isDestructiveScore = (score: number) => score < 50;

  // Group findings by severity
  const errorFindings = result.findings.filter(f => f.severity === 'error');
  const warningFindings = result.findings.filter(f => f.severity === 'warning');
  const infoFindings = result.findings.filter(f => f.severity === 'info');

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="flex items-center gap-3">
            <Settings2 className="h-6 w-6" />
            Settings Health Check
          </DialogTitle>
          <DialogDescription>
            Review potential issues and optimisation opportunities for your timesheet settings
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 flex-1 overflow-y-auto">
          {/* Score Badge */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg mb-4">
            <div>
              <div className="text-sm text-muted-foreground mb-1">Health Score</div>
              <div className="flex items-baseline gap-2">
                <span
                  className={`text-4xl font-bold ${isDestructiveScore(result.score) ? 'text-destructive' : ''}`}
                  style={isDestructiveScore(result.score) ? undefined : getScoreStyle(result.score)}
                >
                  {result.score}
                </span>
                <span className="text-2xl text-muted-foreground">/100</span>
              </div>
              <div
                className={`text-sm font-medium mt-1 ${isDestructiveScore(result.score) ? 'text-destructive' : ''}`}
                style={isDestructiveScore(result.score) ? undefined : getScoreStyle(result.score)}
              >
                {getScoreLabel(result.score)}
              </div>
            </div>
            <div className="flex gap-6 text-sm">
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-2 mb-1">
                  <XCircle className="h-4 w-4 text-destructive" />
                  <span className="font-semibold">{result.summary.errors}</span>
                </div>
                <span className="text-muted-foreground">Errors</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="h-4 w-4" style={{ color: 'var(--warning-500)' }} />
                  <span className="font-semibold">{result.summary.warnings}</span>
                </div>
                <span className="text-muted-foreground">Warnings</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-2 mb-1">
                  <Info className="h-4 w-4 text-blue-500" />
                  <span className="font-semibold">{result.summary.suggestions}</span>
                </div>
                <span className="text-muted-foreground">Suggestions</span>
              </div>
            </div>
          </div>

          {/* Findings List */}
          <div className="space-y-4">
            {result.findings.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="h-16 w-16 mx-auto mb-4" style={{ color: 'var(--success-500)' }} />
                <h3 className="text-lg font-semibold mb-2">Perfect Configuration!</h3>
                <p className="text-muted-foreground">
                  No issues found. Your settings are optimised and compliant.
                </p>
              </div>
            ) : (
              <>
                {/* Errors Section */}
                {errorFindings.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-destructive mb-3 flex items-center gap-2">
                      <XCircle className="h-4 w-4" />
                      Critical Issues ({errorFindings.length})
                    </h3>
                    <div className="space-y-3">
                      {errorFindings.map(finding => (
                        <FindingCard 
                          key={finding.id} 
                          finding={finding} 
                          onApplyFix={onApplyFix}
                          getSeverityIcon={getSeverityIcon}
                          getCategoryLabel={getCategoryLabel}
                          getCategoryColor={getCategoryColor}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Warnings Section */}
                {warningFindings.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--warning-500)' }}>
                      <AlertTriangle className="h-4 w-4" />
                      Warnings ({warningFindings.length})
                    </h3>
                    <div className="space-y-3">
                      {warningFindings.map(finding => (
                        <FindingCard 
                          key={finding.id} 
                          finding={finding} 
                          onApplyFix={onApplyFix}
                          getSeverityIcon={getSeverityIcon}
                          getCategoryLabel={getCategoryLabel}
                          getCategoryColor={getCategoryColor}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Suggestions Section */}
                {infoFindings.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--info-600)' }}>
                      <Info className="h-4 w-4" />
                      Optimisation Suggestions ({infoFindings.length})
                    </h3>
                    <div className="space-y-3">
                      {infoFindings.map(finding => (
                        <FindingCard 
                          key={finding.id} 
                          finding={finding} 
                          onApplyFix={onApplyFix}
                          getSeverityIcon={getSeverityIcon}
                          getCategoryLabel={getCategoryLabel}
                          getCategoryColor={getCategoryColor}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t">
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Individual Finding Card Component
interface FindingCardProps {
  finding: HealthCheckFinding;
  onApplyFix: (finding: HealthCheckFinding) => void;
  getSeverityIcon: (severity: HealthCheckFinding['severity']) => React.ReactNode;
  getCategoryLabel: (category: HealthCheckFinding['category']) => string;
  getCategoryColor: (category: HealthCheckFinding['category']) => string;
}

const FindingCard = ({ 
  finding, 
  onApplyFix, 
  getSeverityIcon, 
  getCategoryLabel, 
  getCategoryColor 
}: FindingCardProps) => {
  return (
    <Card>
      <CardHeader className="px-4 pt-3 pb-2">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <CardTitle className="text-base">{finding.title}</CardTitle>
          </div>
          {finding.fixAction && (
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => onApplyFix(finding)}
              className="shrink-0"
            >
              Quick Fix
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-4 pt-0 pb-4 space-y-2">
        <p className="text-sm text-muted-foreground">{finding.description}</p>
        <div className="bg-muted/50 p-2 rounded-md border-l-2 border-primary">
          <div className="text-xs font-medium text-muted-foreground mb-0.5">
            Recommendation
          </div>
          <p className="text-sm">{finding.recommendation}</p>
        </div>
      </CardContent>
    </Card>
  );
};
